import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CardProvider } from './contexts/CardContext';
import { AuthProvider } from './contexts/AuthContext';
import { ensureSingleQuote } from './utils/quoteStorage';
import CardActivation from './pages/CardActivation';
import CardSell from './pages/CardSell';
import FinaliseSell from './pages/FinaliseSell';
import NewCustomer from './pages/NewCustomer';
import PurchaseCrypto from './pages/PurchaseCrypto';
import WelcomeRoute from './pages/WelcomeRoute';
import Checkout from './pages/Checkout';
import ThankYou from './pages/ThankYou';
import BalanceDisplay from './pages/BalanceDisplay';
import RedirectToActivation from './pages/RedirectToActivation';
import RedirectToBanxa from './pages/RedirectToBanxa';
import FinalStep from './pages/FinalStep';
import TryAgain from './pages/TryAgain';
import ErrorPage from './pages/ErrorPage';
import NotFound from './pages/NotFound';
import CardRouterPage from './pages/CardRouter';
import CheckVerificationResults from './pages/CheckingVerificationResults';
import EmailValidation from './pages/EmailValidation';
import EmailValidationAuth from './pages/EmailValidationAuth';
import StartVerification from './pages/StartVerification';
import EmailSent from './pages/EmailSent';
import SumsubKyc from './pages/SumsubKyc';
import SumsubWebsdkTest from './pages/SumsubWebsdkTest';
import KycInProcess from './pages/KycInProcess';
import CardNotUsable from './pages/CardNotUsable';
// @mcc-dev-preview:import
import StatusPageLayoutPreview from './pages/StatusPageLayoutPreview';
// Note: ProtectedRoute components are imported but not currently used
// They may be needed for future route protection
import { EmailAuthProtectedRoute } from './components/EmailAuthProtectedRoute';

import './App.css';

function App() {
  // Ensure only one quote exists in localStorage on app initialization
  React.useEffect(() => {
    ensureSingleQuote();
  }, []);

  React.useEffect(() => {
    const handleLocationChange = () => {
      // Navigation change handler (no-op for production)
    };
    
    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);
    
    // Initial log
    handleLocationChange();
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);
  
  return (
    <div className="App">
    
    <Router>
      <AuthProvider>
        <CardProvider>
          {/* When adding routes here, update scripts/dev-pages.ts so they appear in the dev page launcher (npm run dev:page). */}
          <Routes>
            {/* Test route to verify routing works */}
            <Route path="/test" element={<div>Test route works!</div>} />
            {/* @mcc-dev-preview:route /dev/status-page */}
            <Route path="/dev/status-page" element={<StatusPageLayoutPreview />} />
            
            {/* Legacy routes - specific routes must come before parameterized routes */}
            <Route path="/card/activate" element={<CardActivation />} />
            <Route path="/card/sell" element={<CardSell />} />
            
            {/* Main card router - entry point for /card/<cardId> */}
            <Route path="/card/:cardId" element={<CardRouterPage />} />
            
            {/* Verification results page - standalone route */}
            <Route path="/check-verification-results" element={<CheckVerificationResults />} />
            
            {/* Email validation pages */}
            <Route path="/validate-email-auth" element={<EmailValidationAuth />} />
            <Route path="/validate-email" element={<EmailValidation />} />
            
            {/* Email verification page */}
            <Route path="/start-verification" element={<StartVerification />} />

            <Route
              path="/kyc-verification"
              element={
                <EmailAuthProtectedRoute
                  pageTitle="Identity verification"
                  pageDescription="Email verification required to continue."
                >
                  <SumsubKyc />
                </EmailAuthProtectedRoute>
              }
            />

            <Route
              path="/kyc-in-process"
              element={
                <EmailAuthProtectedRoute
                  pageTitle="KYC in process"
                  pageDescription="Identity verification is currently in progress."
                >
                  <KycInProcess />
                </EmailAuthProtectedRoute>
              }
            />

            <Route
              path="/scanner-test"
              element={<SumsubWebsdkTest />}
            />

            <Route
              path="/card-not-usable"
              element={
                <EmailAuthProtectedRoute
                  pageTitle="Card not usable"
                  pageDescription="We couldn't complete your identity verification."
                >
                  <CardNotUsable />
                </EmailAuthProtectedRoute>
              }
            />

            {/* Email confirmation page */}
            <Route path="/email-sent" element={<EmailSent />} />
            
            {/* New customer / card state-based routes. /welcome shows Welcome landing without cardId, PurchaseCrypto with cardId; /purchase-crypto is email-protected. */}
            <Route path="/welcome" element={<WelcomeRoute />} />
            <Route path="/new-customer" element={<NewCustomer />} />
            <Route path="/purchase-crypto" element={
              <EmailAuthProtectedRoute>
                <PurchaseCrypto />
              </EmailAuthProtectedRoute>
            } />
            <Route path="/checkout/:orderId" element={<Checkout />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/balance-display" element={<BalanceDisplay />} />
            <Route path="/redirect-to-activation" element={
              <EmailAuthProtectedRoute
                pageTitle="Accessing Protected Zone"
                pageDescription="Email Validation Required"
              >
                <RedirectToActivation />
              </EmailAuthProtectedRoute>
            } />
            <Route path="/redirect-to-banxa" element={<RedirectToBanxa />} />
            <Route path="/card-activation" element={<CardActivation />} />
            <Route path="/final-step" element={
              <EmailAuthProtectedRoute
                pageTitle="Accessing Final Step"
                pageDescription="Email verification required to continue."
              >
                <FinalStep />
              </EmailAuthProtectedRoute>
            } />
            <Route path="/try-again" element={<TryAgain />} />
            <Route path="/card-sell" element={
              <EmailAuthProtectedRoute
                pageTitle="Accessing Protected XXX"
                pageDescription="Email Validation Required"
              >    
              <CardSell />
              </EmailAuthProtectedRoute>
            } />
            <Route path="/finalise-sell" element={<FinaliseSell />} />
            {/* <Route path="/redirect-to-activation" element={
              <RedirectToActivationRoute>
                <RedirectToActivation />
              </RedirectToActivationRoute>
            } /> */}
            <Route path="/error-page" element={<ErrorPage />} />
            
            {/* Default route */}
            <Route path="/" element={
              <Navigate to="/welcome" replace />
            } />
            
            {/* Catch-all route for debugging */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </CardProvider>
      </AuthProvider>
    </Router>
     </div>
  );
}

export default App;
